/**
 * Try to clean up the string value, which can be freely entered by the user
 * and convert it to a number.
 *
 * Find the last possible decimal separator symbol, if the string has multiple
 * of these symbols, just remove them (e.g 123.456.789 => 123456789). If it is
 * the only symbol, treat it as the decimal separator (e.g 123.456,789 =>
 * 123456.789). Remove everything else that isn't the decimal separator
 *
 * Note: This logic is copied from the monolith `convertStringToDbNumber`
 * function.
 */
export const parseUserInputNumber = (input: string): number => {
	let value = input;

	let isNegative = false;
	if (value[0] === '-') {
		isNegative = true;
		value = value.substring(1);
	}

	const decimalSeparatorRe = new RegExp("[.,\\/\\-']", 'g');
	const nonNumericRe = /[^0-9]/g;

	const matches = [];
	let match;
	// eslint-disable-next-line no-cond-assign
	while ((match = decimalSeparatorRe.exec(value))) {
		matches.push(match);
	}

	let replaced = false;
	if (matches.length > 0) {
		const lastMatch = matches[matches.length - 1];
		const separator = lastMatch[0];

		// The string has only one possible separator symbol or the last
		// separator symbol is used only once - replace it with .
		if (
			matches.length === 1 ||
			value.indexOf(separator) === lastMatch.index
		) {
			value = value.replace(nonNumericRe, (match) => {
				return match === separator ? '.' : '';
			});
			replaced = true;
		}
	}

	if (!replaced) {
		// Just remove all non-numeric symbols
		value = value.replace(nonNumericRe, '');
	}

	if (isNegative) {
		value = `-${value}`;
	}

	return parseFloat(value);
};

/**
 * Parse the user inputted number, but return undefined instead of NaN.
 *
 * This is usually more useful for forms and using with SuiAmountInput, for
 * example.
 */
export const parseUserInputNumberNeverNaN = (
	input: string
): number | undefined => {
	const parsed = parseUserInputNumber(input);

	if (isNaN(parsed)) {
		return undefined;
	}

	return parsed;
};
